import * as React from 'react';
import type { WrappedFieldArrayProps } from 'redux-form';
import { Field } from 'redux-form';

import { filterMap } from '@acceligentllc/shared/utils/array';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { PlainUnitEnum, QuantityUnitLabel, isQuantityUnit } from '@acceligentllc/shared/enums/quantityUnit';

import { MAX_BILLABLE_WORK_INFORMATION_FIELDS } from 'ab-common/constants/value';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import type { FormModel } from '../FormModel';
import { renderInformationFieldFromJsonForm } from './helpers';
import styles from './styles.module.scss';

type OriginalUnits = {
	[key: string]: Nullable<string>;
};

type Props = WrappedFieldArrayProps<FormModel['informationalFields'][0]> & { originalUnits: OriginalUnits; };

interface DropdownOption<T> {
	id: T;
	label: string;
}

const compoundKeys = Object.keys(PlainUnitEnum) as PlainUnitEnum[];
const _quantityUnitOptions = filterMap(compoundKeys,
	(_unit) => !isQuantityUnit(QuantityUnitLabel[_unit]),
	(_unit): DropdownOption<QuantityUnitType> => ({
		id: _unit,
		label: QuantityUnitLabel[_unit],
	})
);
const quantityUnitOptions = [
	{ id: ' ', label: <span className={styles.dropdown_empty}>None</span> },
	..._quantityUnitOptions,
];

const AddBillableWorkModalInformationalField: React.FC<Props> = (props) => {
	const { fields, originalUnits } = props;

	const onAddInformationalFieldClick = React.useCallback(() => fields.push({ name: null, values: null, units: null }), [fields]);
	const onRemoveInformationalFieldClick = React.useCallback((index) => () => {
		const indexField = fields.getAll()[index];

		if (indexField.name) {
			fields.splice(index, 1, { name: indexField.name, values: '', units: indexField.units ? ' ' : null });
		} else {
			fields.remove(index);
		}
	}, [fields]);
	const filterUnits = React.useCallback((option: DropdownOption<QuantityUnitType>, searchText: string) => {
		return option.label.toLowerCase().includes(searchText);
	}, []);
	const onUnitClear = React.useCallback((index: number) => () => {
		const indexField = fields.getAll()[index];
		fields.splice(index, 1, { ...indexField, units: ' ' });
	}, [fields]);

	const fieldUnits = React.useMemo(() => fields.getAll()?.map((_field) => _field?.units), [fields]);
	const fieldNames = React.useMemo(() => fields.getAll()?.map((_field) => _field?.name), [fields]);

	const formatField = React.useCallback((value) => {
		return renderInformationFieldFromJsonForm(value);
	}, []);

	const renderField = (field: string, index: number) => {
		const units = fieldUnits[index]?.split(', ');
		const ogUnits = originalUnits[`i${index + 1}`]?.split(', ');
		const showUnitField = Boolean(fieldUnits[index]) || Boolean(originalUnits[`i${index + 1}`]);

		return (
			<>
				<div>{fieldNames[index] ?? ''}</div>
				<div className={styles['add-billable-work-modal__info-def-item']} key={field}>
					<Field
						component={Input}
						format={formatField}
						id="informationalField"
						label={`Informational Field ${index + 1}`}
						name={`${field}.values`}
						placeholder="Add informational field"
						type="text"
					/>
					{showUnitField && ((units && units.length > 1) || (ogUnits && ogUnits.length > 1) ?
						<div className={styles['add-billable-work-modal__info-def-item__unit-field']}>
							<Field
								className={styles['unit-price']}
								component={Input}
								id="unit"
								label={'Unit'}
								name={`${field}.units`}
								placeholder="Add Unit"
								type="text"
							/>
						</div> :
						<div className={styles['add-billable-work-modal__info-def-item__unit-field']}>
							<Field
								className={styles['unit-price']}
								component={Dropdown}
								defaultValue={null}
								disabled={false}
								filterable={true}
								filterBy={filterUnits}
								fixed={true}
								id="unit"
								label={'Unit'}
								labelKey="label"
								name={`${field}.units`}
								onClear={onUnitClear(index)}
								options={quantityUnitOptions}
								placeholder="Add Unit"
								valueKey="id"
								withCaret={true}
							/>
						</div>
					)}
					<Button
						icon="Delete"
						onClick={onRemoveInformationalFieldClick(index)}
						style="link-danger"
					/>
				</div>
			</>
		);
	};
	return (
		<>
			{fields.map(renderField)}
			{fields.length < MAX_BILLABLE_WORK_INFORMATION_FIELDS &&
				<Button
					icon="ActionPlusSmall"
					label="Add Informational Field"
					onClick={onAddInformationalFieldClick}
					style="link"
				/>
			}
		</>
	);
};

export default AddBillableWorkModalInformationalField;
